import * as React from "react";
import {
  Button,
  Stack,
  Radio,
  RadioGroup,
  Heading,
  Alert,
  TextLink,
  Text,
} from "@origin-digital/ods-core";
import RequestingCompany from "../FormComponents/RequestingCompany";
import {
  FieldProps,
  FormikProps,
  Field,
  useFormikContext,
  yupToFormErrors,
} from "formik";
import ReadOnlyTextField from "../FormComponents/ReadOnlyTextField";
import { useAppContext } from "src/context/AppContext";

import RemoveSupplyContext from "../../../context/RemoveSupplyContext";
import { ErrorTypeEnum } from "../types";
import { getFormattedAddress } from "src/components/Premises/helpers";
import StatusAlert, {
  MirnStatusEnum,
  NmiStatusEnum,
} from "../ChangeRequest/StatusAlert";
import Nmi from "../ChangeRequest/Nmi";
import {
  energyTypeOptions,
  mapRequestingCompanyToResponse,
} from "../ChangeRequest/helpers";
import { removeSupplyValidationSchema } from "./validationSchema";
import FormFieldLayout from "../FormComponents/FormFieldLayout";
import { apiClient } from "src/api/apiService";
import { PremiseDetails } from "src/components/Premises/types";
import { useState } from "react";
import Mirn from "../ChangeRequest/Mirn";
import { RemoveSupplyFormValues } from "./types";

const RemoveSupplyPremiseBlock: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    setDisplayingSnackbar,
    setPremiseAddress,
    setDisplayingAlertBanner,
    setActiveSection,
    setDraftInfo,
  } = React.useContext(RemoveSupplyContext);
  const {
    context: { user, featureFlags },
  } = useAppContext();

  const { values, errors, setFieldValue, submitForm, validateForm, setErrors } =
    useFormikContext<RemoveSupplyFormValues>();
  const { noCompany } = values;

  React.useEffect(() => {
    if (Object.keys(errors).length > 0) {
      //Check all the fields within the whole form. If any field has an error, display the error banner.
      setDisplayingAlertBanner(ErrorTypeEnum.OCP_FIELD_VALIDATION);
    } else {
      setDisplayingAlertBanner(null);
    }
  }, [errors]);

  const requireAdditionalInfo = React.useMemo(() => {
    const energyTypesThatRequireAdditionalInfo = ["electricity"];
    if (featureFlags.enableRemoveSupplyForNaturalGas) {
      energyTypesThatRequireAdditionalInfo.push("naturalGas");
    }
    return energyTypesThatRequireAdditionalInfo;
  }, [featureFlags.enableRemoveSupplyForNaturalGas]);

  const { nmiStatus, mirnStatus, energyType, premise, nmi, mirn } = values;
  const isBlockedMirnForWA = mirn && mirn.startsWith("5600");
  const isCreationEnabled =
    (!!nmi && nmiStatus === NmiStatusEnum.ELIGIBLE) ||
    (!!mirn && mirnStatus === MirnStatusEnum.ELIGIBLE && !isBlockedMirnForWA);

  const onClickCreatePremise = () => {
    removeSupplyValidationSchema
      .validate(values, {
        abortEarly: false,
        context: {
          noCompany,
        },
      })
      .then(async () => {
        setIsLoading(true);
        const updatedPremiseWithRequestingCompany = {
          ...values.premise,
          site_details: {
            ...values.premise?.site_details,
            requesting_company: values.requestingCompany
              ? mapRequestingCompanyToResponse(values.requestingCompany)
              : null,
          },
        };
        const { data: draftData } = await apiClient.post("/draft/premise", {
          premise: updatedPremiseWithRequestingCompany,
          services: [
            {
              service_type:
                energyType === "electricity" ? "ELECTRICITY" : "GAS ",
              request_type: "REMOVE_SUPPLY",
              payload: {},
              nmi_mirn: energyType === "electricity" ? values.nmi : values.mirn,
            },
          ],
        });
        setDraftInfo({
          draft_id: draftData.services[0].draft_id,
          draft_version: draftData.services[0].draft_version,
          request_id: draftData.services[0].request_id,
          raw_request_status: draftData.services[0].raw_request_status,
          request_type: draftData.services[0].request_type,
          message_enabled: draftData.services[0].message_enabled,
        });
        setFieldValue("noCompany", noCompany);
        setFieldValue("premise", draftData.premise);
        setPremiseAddress(
          premise ? getFormattedAddress(premise as PremiseDetails) : null
        );
        setDisplayingSnackbar({
          message: `Premises has been submitted.`,
          tone: "positive",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setActiveSection("premiseReadonlySection");
      })
      .catch((e) => {
        setErrors(yupToFormErrors(e));
        setDisplayingSnackbar({
          message: `Couldn't create premises`,
          tone: "caution",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEnergyTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue("energyType", e.target.value);
    setFieldValue("nmi", "");
    setFieldValue("mirn", "");
    setFieldValue("nmiStatus", null);
    setFieldValue("mirnStatus", null);
    setFieldValue("premise", null);
  };

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
    >
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
        id="premiseSection"
      >
        <Heading variant="h4">Premises</Heading>

        {noCompany && (
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Name"
              value={
                values.requesterName || `${user?.first_name} ${user?.last_name}`
              }
            />
          </FormFieldLayout>
        )}

        {noCompany && (
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Email"
              value={values.requesterEmail || user?.email || ""}
            />
          </FormFieldLayout>
        )}

        {!noCompany && (
          <FormFieldLayout>
            <RequestingCompany
              fieldName="Company"
              error={errors["requestingCompany"]}
              setFieldValue={setFieldValue}
              submitForm={submitForm}
              validateForm={validateForm}
              isEditing={false}
              requestingCompany={values.requestingCompany}
              fullWidth
              setNoCompany={() => setFieldValue("noCompany", true)}
            />
          </FormFieldLayout>
        )}

        <FormFieldLayout multipleFields>
          <Field name="energyType">
            {({ field }: { field: FormikProps<FieldProps> }) => (
              <RadioGroup
                {...field}
                id="energyType"
                name="energyType"
                label="Which energy type?"
                defaultValue="electricity"
                onChange={(e) => {
                  setFieldValue("energyType", e.target.value);
                  setFieldValue("nmi", "");
                  setFieldValue("mirn", "");
                  setFieldValue("nmiStatus", null);
                  setFieldValue("mirnStatus", null);
                }}
              >
                {energyTypeOptions.map(({ id, label, value }) => (
                  <Radio
                    key={id}
                    id={id}
                    label={label}
                    value={value}
                    onChange={handleEnergyTypeChange}
                  />
                ))}
              </RadioGroup>
            )}
          </Field>
          {!featureFlags.enableRemoveSupplyForNaturalGas &&
            energyType === "naturalGas" && (
              <Alert title="Sorry you can’t use this form to remove supply for Natural gas">
                <Text>
                  To remove your Natural gas supply, please call 132 461.
                </Text>
              </Alert>
            )}
          {energyType === "lpg" && (
            <Alert title="Sorry you can’t use this form to remove supply for LPG">
              <Text>
                To remove your LPG supply,{" "}
                <TextLink
                  href="https://www.originenergy.com.au/lpg/"
                  target="_blank"
                >
                  visit Origin LPG
                </TextLink>
              </Text>
            </Alert>
          )}
        </FormFieldLayout>
        {/* TODO: update for gas abolishment release */}
        {requireAdditionalInfo.includes(energyType) && (
          <Stack
            space={{
              xs: "xlarge",
              md: "xxlarge",
            }}
          >
            {energyType === "electricity" && (
              <FormFieldLayout text="The National Metering Identifier (NMI) can be found on the latest electricity bill. ">
                <Nmi requestType="REMOVE_SUPPLY" />
              </FormFieldLayout>
            )}
            {energyType === "naturalGas" && (
              <FormFieldLayout text="The Meter Installation Registration Number (MIRN) can be found on the latest gas bill.">
                <Mirn />
              </FormFieldLayout>
            )}
            {nmiStatus || mirnStatus || premise ? (
              <FormFieldLayout multipleFields>
                {nmiStatus && (
                  <StatusAlert
                    status={nmiStatus}
                    type="nmi"
                    requestType="REMOVE_SUPPLY"
                  />
                )}
                {isBlockedMirnForWA ? (
                  <Alert title="Sorry, you can’t use this form to change or remove supply at this address.">
                    <Text>
                      Please contact submit a request to{" "}
                      <TextLink
                        href="https://atco.outsystemsenterprise.com/Disconnection/"
                        target="_blank"
                      >
                        ATCO
                      </TextLink>{" "}
                      to remove the gas supply.
                    </Text>
                  </Alert>
                ) : (
                  mirnStatus && <StatusAlert status={mirnStatus} type="mirn" />
                )}
                {premise && (
                  <ReadOnlyTextField
                    label="Address"
                    value={getFormattedAddress(premise)}
                  />
                )}
              </FormFieldLayout>
            ) : null}
          </Stack>
        )}
      </Stack>

      <Button
        onClick={onClickCreatePremise}
        disabled={!isCreationEnabled}
        spinner={isLoading}
      >
        Create premises
      </Button>
    </Stack>
  );
};

export default RemoveSupplyPremiseBlock;
